import type {
    ComponentChildren,
    JSX,
} from "preact";
import {
    forwardRef,
} from "preact/compat";
import {
    LoadingSpinner,
} from "./LoadingSpinner.tsx";

export type ButtonVariant =
    | "primary"
    | "secondary"
    | "success"
    | "error"
    | "ghost";
export type ButtonSize = "sm" | "md" | "lg";

export type ButtonClassOptions = {
    variant?: ButtonVariant;
    size?: ButtonSize;
    className?: string;
};

// `href` is additive rather than a discriminated union: a union distributes
// badly through forwardRef and drops `disabled` from the button case, which
// every existing consumer passes.
type ButtonProps = JSX.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: ButtonVariant;
    size?: ButtonSize;
    isLoading?: boolean;
    leftIcon?: ComponentChildren;
    rightIcon?: ComponentChildren;
    // when set, renders an <a> carrying the same recipe
    href?: string;
    target?: string;
    rel?: string;
};

const baseStyles =
    "inline-flex items-center justify-center gap-2 rounded font-medium transition-colors duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed";

const variantStyles: Record<ButtonVariant, string> = {
    primary: "bg-accent text-white hover:bg-accent-hover",
    secondary:
        "bg-bg-tertiary text-text-primary border border-border hover:bg-border",
    success: "bg-success text-white hover:bg-success-hover",
    error: "bg-error text-white hover:bg-error-hover",
    ghost:
        "bg-transparent text-text-secondary hover:text-text-primary hover:bg-bg-tertiary",
};

// md/lg clear the 44px pointer-target floor; sm stays compact for the dense
// admin toolbars that were built around it.
const sizeStyles: Record<ButtonSize, string> = {
    sm: "px-2 py-1 text-sm",
    md: "px-4 py-2 min-h-[2.75rem]",
    lg: "px-6 py-3 text-lg min-h-[2.75rem]",
};

// The single source of the button recipe. Exported so a router <Link> or any
// other non-<button> element can be styled as a button without hand-copying
// the class string.
export const buttonClasses = ({
    variant = "primary",
    size = "md",
    className = "",
}: ButtonClassOptions = {}): string =>
    `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`
        .trim().replace(/\s+/g, " ");

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({
        variant = "primary",
        size = "md",
        isLoading,
        leftIcon,
        rightIcon,
        className = "",
        children,
        href,
        disabled,
        ...props
    }, ref) => {
        const classes = buttonClasses({
            variant,
            size,
            className: className as string,
        });
        const content = (
            <>
                {isLoading ? <LoadingSpinner size="sm" /> : leftIcon}
                {children}
                {!isLoading && rightIcon}
            </>
        );

        if (href !== undefined) {
            const inert = disabled || isLoading;
            return (
                <a
                    ref={ref as unknown as JSX.AnchorHTMLAttributes<
                        HTMLAnchorElement
                    >["ref"]}
                    // an anchor has no disabled attribute — dropping href is
                    // what takes it out of the tab order and stops navigation
                    href={inert ? undefined : href}
                    aria-disabled={inert ? "true" : undefined}
                    className={inert
                        ? `${classes} opacity-50 cursor-not-allowed`
                        : classes}
                    {...props as JSX.AnchorHTMLAttributes<HTMLAnchorElement>}
                >
                    {content}
                </a>
            );
        }

        return (
            <button
                ref={ref}
                className={classes}
                disabled={disabled || isLoading}
                {...props}
            >
                {content}
            </button>
        );
    },
);
